import {
  Avatar,
  AvatarColorOption,
  CollapseCard,
  FlexBox,
  NavigationButton,
} from '@dixa/lib-shared-habla';
import { User } from 'lib-shared-types-api';
import { memo, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createSearchUrl } from 'app/views/layout/views/search/utils';
import {
  UserActivityLogFeed,
  UserActivityLogFiltersValue,
} from 'components/activityLog/components/userActivityLogFeed';
import { UserActivityLayoutContext } from 'components/activityLog/components/userActivityTypes/userActivityLayoutContext';
import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';

import { CardMoveHandle } from '../../components/cardMoveHandle/cardMoveHandle';
import { VisibilityToggle } from '../../components/visibilityToggle/visibilityToggle';

import styles from './timelineCard.module.scss';
import { TimelineFilters } from './timelineFilters';

import '../../../../styles/cards.less';

export const TIMELINE_ICON_COLORS: AvatarColorOption = {
  backgroundColor: 'var(--dixa-avatar-background-blue)',
  contentColor: 'var(--dixa-avatar-content-blue)',
};

type TimelineCardProps = Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove'
> & {
  readonly requester: User;
  readonly isOpen: boolean;
  readonly isMinimized?: boolean;
  readonly onToggle: () => void;
};

/** Shows the requester's activity log in the conversation sidebar. */
export const TimelineCard = memo(function TimelineCard({
  requester,
  isOpen,
  isMinimized = false,
  onToggle,
  index,
  type,
  onMove,
}: TimelineCardProps): JSX.Element {
  const row = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<UserActivityLogFiltersValue>({});

  const handleFiltersChange = useCallback(
    (changed: Partial<UserActivityLogFiltersValue>) =>
      setFilters((current) => ({ ...current, ...changed })),
    [],
  );

  const handleClearFilters = useCallback(() => setFilters({}), []);

  const handleShowAllConversations = useCallback(
    () =>
      navigate(
        createSearchUrl({
          filters: {
            strategy: 'all',
            filters: [
              {
                field: 'requested_by',
                operator: 'is_one',
                values: [{ label: '', value: requester.id }],
              },
            ],
          },
        }),
      ),
    [navigate, requester.id],
  );

  const avatar = <Avatar icon="history" color={TIMELINE_ICON_COLORS} />;

  if (isMinimized) {
    return <div className="icon-holder">{avatar}</div>;
  }

  return (
    <CollapseCard containerRef={row} onToggle={onToggle} isOpen={isOpen}>
      <CollapseCard.Header
        avatar={avatar}
        slotBeforeTitle={
          <CardMoveHandle
            index={index}
            target={row}
            type={type}
            onMove={onMove}
          />
        }
        slotAfterTitle={
          <>
            <TimelineFilters filters={filters} onChange={handleFiltersChange} />
            <VisibilityToggle />
          </>
        }
      >
        Timeline
      </CollapseCard.Header>
      <CollapseCard.Content>
        <UserActivityLayoutContext.Provider value="compact">
          <div className={styles.feed}>
            <UserActivityLogFeed
              user={requester}
              filters={filters}
              onClearFilters={handleClearFilters}
            />
          </div>
        </UserActivityLayoutContext.Provider>
      </CollapseCard.Content>
      <CollapseCard.Content>
        <FlexBox justifyContent="center" padding="200 0 300 0">
          <NavigationButton
            className="card-action-btn"
            navigate={handleShowAllConversations}
          >
            Show all conversations
          </NavigationButton>
        </FlexBox>
      </CollapseCard.Content>
    </CollapseCard>
  );
});
